/**
 * Plugin TinyMCE — Componentes da Biblioteca AI  (v2 — Drill-Down)
 *
 * Funcionalidades:
 *  • Detecção DINÂMICA de componentes via /ai/component-detection-config.
 *  • Marcação multi-nível: todos os componentes são detectados em qualquer
 *    profundidade de aninhamento (data-ai-comp-depth).
 *  • Auto-detect drill-down: ao clicar numa região que contém um sub-componente
 *    dentro de um bloco raiz, o bloco raiz é "entrado" automaticamente e o
 *    sub-componente é selecionado — sem botão "Entrar" explícito.
 *  • Pilha de contexto (enteredStack): suporta N níveis de profundidade.
 *  • ESC sobe um nível; click fora sai de todos os níveis.
 *  • Context toolbars:
 *      – Raiz: Editar | Duplicar | ↑ | ↓ | Excluir
 *      – Interno: Editar | ▼Adicionar | Duplicar | Excluir
 *      – Badge:  ▼Adicionar | Duplicar | Excluir
 *  • Menu "Adicionar" (dropdown):
 *      – Duplicar e editar
 *      – Inserir da biblioteca
 *      – Inserir vazio
 *  • Duplo-clique em badge/leaf: edição inline de texto.
 *  • Dentro de bloco "entrado", áreas de texto ficam editáveis diretamente.
 *  • Filtros input/output limpam atributos de marcação ao salvar.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════════

var BLOCK_ATTR = 'data-ai-comp-block';
var SPAN_ATTR = 'data-ai-comp-span';
var DEPTH_ATTR = 'data-ai-comp-depth';
var ENTERED_ATTR = 'data-ai-entered';

var BLOCK_SEL = '[' + BLOCK_ATTR + ']';
var SPAN_SEL = '[' + SPAN_ATTR + ']';

// ── Fallback estático ─────────────────────────────────────────────────────────
var FALLBACK_BLOCK_CLASSES = [
    'cover', 'flow-box', 'endpoint', 'section-divider', 'decision',
    'deploy-box', 'sec-list', 'arch', 'card', 'action-card',
    'cmd-block', 'page-header', 'badges', 'steps', 'metrics', 'kpi',
    'flow', 'persona-card', 'tool-grid', 'tool-card', 'tool-block',
    'legend', 'hierarchy', 'footer', 'list', 'feature-list', 'alert',
    'env-req-box', 'cards', 'metric-card', 'checklist',
    'file-tree', 'code-example', 'business-quote',
    'alert-warn', 'alert-green', 'params-table',
];
var FALLBACK_SPAN_CLASSES = [
    'badge', 'badge-new', 'badge-change', 'badge-ok',
    'badge-blue', 'badge-green', 'badge-purple', 'badge-orange',
    'test-pass', 'status-badge',
];
var FALLBACK_BLOCK_TAGS = ['div', 'ul', 'ol', 'pre', 'blockquote', 'details'];

// Tags de texto editável (double-click inline)
var TEXT_TAGS = new Set([
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'td', 'th',
    'cite', 'strong', 'em', 'a', 'code', 'span',
]);
var EDITABLE_CLASSES = new Set([
    'action-num', 'subtitle', 'm-value', 'm-label', 'sd-badge',
    'tool-name', 'hier-desc', 'hier-box', 'decision-title',
    'endpoint-url', 'method', 'num', 'flow-content',
    'tool-block-name', 'tool-block-file',
    'sys-badge', 'check-yes', 'check-no', 'check-wait',
]);

// ═══════════════════════════════════════════════════════════════════════════════
// ESTADO DINÂMICO
// ═══════════════════════════════════════════════════════════════════════════════

var blockClassSet = new Set(FALLBACK_BLOCK_CLASSES);
var spanClassSet = new Set(FALLBACK_SPAN_CLASSES);
var blockTagSet = new Set(FALLBACK_BLOCK_TAGS);
var configLoaded = false;

/** Pilha de blocos "entrados" (do mais externo para o mais interno) */
var enteredStack = [];
/** Elemento em edição inline (double-click em texto) */
var activeInlineEdit = null;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIG DINÂMICA
// ═══════════════════════════════════════════════════════════════════════════════

function loadDetectionConfig() {
    if (configLoaded) return Promise.resolve();
    var csrfMeta = document.querySelector('meta[name="csrf-token"]');
    var csrfToken = csrfMeta ? csrfMeta.content : '';
    return fetch('/ai/component-detection-config', {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken },
    })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (d) {
            if (Array.isArray(d.blockClasses) && d.blockClasses.length) blockClassSet = new Set(d.blockClasses);
            if (Array.isArray(d.spanClasses) && d.spanClasses.length) spanClassSet = new Set(d.spanClasses);
            if (Array.isArray(d.blockTags) && d.blockTags.length) blockTagSet = new Set(d.blockTags);
            configLoaded = true;
        })
        .catch(function (e) {
            console.warn('[AI Components] Config fetch failed, using fallback:', e.message);
            configLoaded = true;
        });
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETECÇÃO DE COMPONENTES
// ═══════════════════════════════════════════════════════════════════════════════

function isAiBlock(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute(BLOCK_ATTR)) return true;
    var tag = (el.tagName || '').toLowerCase();
    if (!blockTagSet.has(tag)) return false;
    var cls = el.className;
    if (!cls || typeof cls !== 'string') return false;
    var parts = cls.trim().split(/\s+/);
    for (var i = 0; i < parts.length; i++) { if (blockClassSet.has(parts[i])) return true; }
    return false;
}

function isAiBadge(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute(SPAN_ATTR)) return true;
    var tag = (el.tagName || '').toLowerCase();
    if (tag !== 'span') return false;
    var cls = el.className;
    if (!cls || typeof cls !== 'string') return false;
    var parts = cls.trim().split(/\s+/);
    for (var i = 0; i < parts.length; i++) { if (spanClassSet.has(parts[i])) return true; }
    return false;
}

/** Conta ancestrais AI (block ou span) até o body. */
function computeDepth(el, body) {
    var depth = 0;
    var p = el.parentElement;
    while (p && p !== body) {
        if (p.hasAttribute(BLOCK_ATTR) || p.hasAttribute(SPAN_ATTR)) depth++;
        p = p.parentElement;
    }
    return depth;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MARCAÇÃO MULTI-NÍVEL
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Marca TODOS os componentes AI em qualquer profundidade.
 * Apenas depth-0 recebem contenteditable="false" no estado inicial.
 * MELHORIA: Zonas de texto editável (H1, P, etc) são deixadas como ce=true 
 * mesmo dentro de blocos ce=false, permitindo edição direta no TinyMCE.
 */
function markAllComponents(editor) {
    var body = editor.getBody();
    if (!body) return;

    // Monta seletores de tag para blocos
    var tagSels = [];
    blockTagSet.forEach(function (t) { tagSels.push(t + '[class]'); });

    // ── Blocos ──────────────────────────────────────────────────────────────
    if (tagSels.length > 0) {
        var els = body.querySelectorAll(tagSels.join(','));
        for (var i = 0; i < els.length; i++) {
            var el = els[i];
            if (el.hasAttribute(BLOCK_ATTR)) continue;
            if (!isAiBlock(el)) continue;

            el.setAttribute(BLOCK_ATTR, 'true');
            var d = computeDepth(el, body);
            el.setAttribute(DEPTH_ATTR, String(d));

            if (d === 0 && !el.hasAttribute(ENTERED_ATTR)) {
                el.contentEditable = 'false';
            }
        }
    }

    // ── Badges (span) ───────────────────────────────────────────────────────
    var spans = body.querySelectorAll('span[class]');
    for (var j = 0; j < spans.length; j++) {
        var sp = spans[j];
        if (sp.hasAttribute(SPAN_ATTR)) continue;
        if (!isAiBadge(sp)) continue;

        sp.setAttribute(SPAN_ATTR, 'true');
        var ds = computeDepth(sp, body);
        sp.setAttribute(DEPTH_ATTR, String(ds));

        if (ds === 0) {
            sp.contentEditable = 'false';
        }
    }

    // ── PROTECTED ISLANDS: Libera elementos de texto para edição natural ────
    // Procura por tags de texto dentro de componentes marcados como ce=false
    var ceFalseObjs = body.querySelectorAll('[contenteditable="false"]');
    for (var m = 0; m < ceFalseObjs.length; m++) {
        var container = ceFalseObjs[m];
        // Libera H1..H6, P, e áreas marcadas como editáveis que sejam filhos
        var islands = container.querySelectorAll('h1,h2,h3,h4,h5,h6,p,td,th,li,.subtitle,.value');
        for (var n = 0; n < islands.length; n++) {
            // Só libera se não for ele mesmo outro componente AI (badge, etc) que tenha sua própria lógica
            if (!islands[n].hasAttribute(BLOCK_ATTR) && !islands[n].hasAttribute(SPAN_ATTR)) {
                islands[n].contentEditable = 'true';
            }
        }
    }

    // ── Recalcula contenteditable para blocos entered ───────────────────────
    for (var k = 0; k < enteredStack.length; k++) {
        var entered = enteredStack[k];
        entered.removeAttribute('contenteditable');
        var children = getDirectChildComponents(entered);
        for (var c = 0; c < children.length; c++) {
            children[c].contentEditable = 'false';
            // Repete o processo de islands para os filhos que foram marcados agora como ce=false
            var subIslands = children[c].querySelectorAll('h1,h2,h3,h4,h5,h6,p,td,th,li,.subtitle,.value');
            for (var si = 0; si < subIslands.length; si++) {
                if (!subIslands[si].hasAttribute(BLOCK_ATTR) && !subIslands[si].hasAttribute(SPAN_ATTR)) {
                    subIslands[si].contentEditable = 'true';
                }
            }
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SISTEMA ENTER / EXIT  (Drill-Down)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Retorna filhos AI diretos de `block` (profundidade block+1).
 * "Direto" = próximo nível de aninhamento AI, sem outro bloco AI intermediário.
 */
function getDirectChildComponents(block) {
    var myDepth = parseInt(block.getAttribute(DEPTH_ATTR) || '0', 10);
    var targetDepth = myDepth + 1;
    var sel = BLOCK_SEL + '[' + DEPTH_ATTR + '="' + targetDepth + '"],'
        + SPAN_SEL + '[' + DEPTH_ATTR + '="' + targetDepth + '"]';
    var all = block.querySelectorAll(sel);
    var out = [];
    for (var i = 0; i < all.length; i++) out.push(all[i]);
    return out;
}

/**
 * "Entra" em um bloco: torna-o editável, seus filhos AI diretos viram
 * contenteditable=false (selecionáveis individualmente).
 */
function enterComponent(editor, block) {
    if (block.hasAttribute(ENTERED_ATTR)) return;
    if (activeInlineEdit) finishInlineEdit(editor);

    block.setAttribute(ENTERED_ATTR, 'true');
    block.removeAttribute('contenteditable');

    var children = getDirectChildComponents(block);
    for (var i = 0; i < children.length; i++) {
        children[i].contentEditable = 'false';
    }

    enteredStack.push(block);
    editor.nodeChanged();
    updateBreadcrumb(editor);
}

/** Sai do nível mais interno na pilha. */
function exitCurrentLevel(editor) {
    if (enteredStack.length === 0) return;
    var block = enteredStack.pop();
    block.removeAttribute(ENTERED_ATTR);
    block.contentEditable = 'false';

    var children = getDirectChildComponents(block);
    for (var i = 0; i < children.length; i++) {
        children[i].removeAttribute('contenteditable');
    }

    try { editor.selection.select(block); } catch (_) { }
    editor.nodeChanged();
    updateBreadcrumb(editor);
}

/** Sai de TODOS os níveis (volta ao estado normal). */
function exitAllLevels(editor) {
    while (enteredStack.length > 0) {
        var block = enteredStack.pop();
        block.removeAttribute(ENTERED_ATTR);
        block.contentEditable = 'false';
        var children = getDirectChildComponents(block);
        for (var i = 0; i < children.length; i++) {
            children[i].removeAttribute('contenteditable');
        }
    }
    editor.nodeChanged();
    updateBreadcrumb(editor);
}

/** Verifica se `el` está dentro de um bloco entrado. */
function hasEnteredAncestor(el) {
    var p = el.parentElement;
    while (p) {
        if (p.hasAttribute && p.hasAttribute(ENTERED_ATTR)) return true;
        p = p.parentElement;
    }
    return false;
}

/**
 * Coleta TODOS os componentes AI na cadeia de ancestrais de `target` até `body`.
 * Retorna array ordenado: [mais_interno, ..., mais_externo].
 * Se o target em si for um componente, ele é incluído.
 */
function collectComponentChain(target, body) {
    var chain = [];
    var el = target;
    while (el && el !== body) {
        if (el.hasAttribute && (el.hasAttribute(BLOCK_ATTR) || el.hasAttribute(SPAN_ATTR))) {
            chain.push(el);
        }
        el = el.parentElement;
    }
    return chain; // [innermost, ..., outermost]
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIMPEZA / SERIALIZAÇÃO
// ═══════════════════════════════════════════════════════════════════════════════

var CLEANUP_ATTRS = [BLOCK_ATTR, SPAN_ATTR, DEPTH_ATTR, ENTERED_ATTR, 'data-ai-edit-id'];

function cleanNodeAttrs(node) {
    for (var i = 0; i < CLEANUP_ATTRS.length; i++) {
        node.attr(CLEANUP_ATTRS[i], null);
    }
    if (node.attr('contenteditable') === 'false') {
        node.attr('contenteditable', null);
    }
}

function setupOutputFilter(editor) {
    var tags = ['div', 'ul', 'ol', 'span', 'pre', 'blockquote', 'details'];
    tags.forEach(function (tag) {
        editor.serializer.addNodeFilter(tag, function (nodes) {
            nodes.forEach(cleanNodeAttrs);
        });
    });
}

function setupInputFilter(editor) {
    var bTags = ['div', 'ul', 'ol', 'pre', 'blockquote', 'details'];
    bTags.forEach(function (tag) {
        editor.parser.addNodeFilter(tag, function (nodes) {
            nodes.forEach(function (node) {
                var cls = node.attr('class') || '';
                var parts = cls.trim().split(/\s+/);
                for (var k = 0; k < parts.length; k++) {
                    if (blockClassSet.has(parts[k])) {
                        node.attr(BLOCK_ATTR, 'true');
                        return;
                    }
                }
            });
        });
    });
    editor.parser.addNodeFilter('span', function (nodes) {
        nodes.forEach(function (node) {
            var cls = node.attr('class') || '';
            var parts = cls.trim().split(/\s+/);
            for (var k = 0; k < parts.length; k++) {
                if (spanClassSet.has(parts[k])) {
                    node.attr(SPAN_ATTR, 'true');
                    return;
                }
            }
        });
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDIÇÃO INLINE  (double-click em texto / badge leaf)
// ═══════════════════════════════════════════════════════════════════════════════

function findEditableZone(target, block) {
    var el = target;
    while (el && el !== block) {
        if (isEditableTextEl(el)) return el;
        el = el.parentElement;
    }
    return null;
}

function isEditableTextEl(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = (el.tagName || '').toLowerCase();
    var cls = el.className || '';
    if (cls && typeof cls === 'string') {
        var parts = cls.trim().split(/\s+/);
        for (var i = 0; i < parts.length; i++) {
            if (EDITABLE_CLASSES.has(parts[i])) return true;
        }
    }
    if (TEXT_TAGS.has(tag)) {
        return el.textContent && el.textContent.trim().length > 0;
    }
    return false;
}

function startInlineEdit(editor, el, block) {
    if (activeInlineEdit) finishInlineEdit(editor);

    activeInlineEdit = { el: el, block: block, originalHtml: el.innerHTML };

    el.setAttribute('contenteditable', 'true');
    el.classList.add('ai-inline-editing');
    el.focus();

    try {
        var range = editor.dom.createRng();
        range.selectNodeContents(el);
        var sel = editor.selection.getSel();
        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
    } catch (_) { }

    el._aiBlurHandler = function () {
        setTimeout(function () { finishInlineEdit(editor); }, 120);
    };
    el.addEventListener('blur', el._aiBlurHandler);

    el._aiKeyHandler = function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); e.stopPropagation(); el.blur();
        }
        if (e.key === 'Escape') {
            e.preventDefault(); e.stopPropagation();
            el.innerHTML = activeInlineEdit ? activeInlineEdit.originalHtml : el.innerHTML;
            el.blur();
        }
    };
    el.addEventListener('keydown', el._aiKeyHandler);
}

function finishInlineEdit(editor) {
    if (!activeInlineEdit) return;
    var el = activeInlineEdit.el;
    if (el._aiBlurHandler) { el.removeEventListener('blur', el._aiBlurHandler); el._aiBlurHandler = null; }
    if (el._aiKeyHandler) { el.removeEventListener('keydown', el._aiKeyHandler); el._aiKeyHandler = null; }
    el.removeAttribute('contenteditable');
    el.classList.remove('ai-inline-editing');
    editor.undoManager.add();
    editor.nodeChanged();
    activeInlineEdit = null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// AÇÕES DOS BOTÕES
// ═══════════════════════════════════════════════════════════════════════════════

function getSelectedBlock(editor) {
    var node = editor.selection.getNode();
    if (!node) return null;
    if (node.hasAttribute && node.hasAttribute(BLOCK_ATTR)) return node;
    return node.closest ? node.closest(BLOCK_SEL) : null;
}

function getSelectedBadge(editor) {
    var node = editor.selection.getNode();
    if (!node) return null;
    if (node.hasAttribute && node.hasAttribute(SPAN_ATTR)) return node;
    return node.closest ? node.closest(SPAN_SEL) : null;
}

/** Componente selecionado: badge tem prioridade sobre bloco. */
function getSelectedAny(editor) {
    var b = getSelectedBadge(editor);
    if (b) return b;
    return getSelectedBlock(editor);
}

function detectComponentTag(el) {
    var cls = el.className || '';
    if (!cls || typeof cls !== 'string') return null;
    var parts = cls.trim().split(/\s+/);
    for (var i = 0; i < parts.length; i++) {
        if (blockClassSet.has(parts[i]) || spanClassSet.has(parts[i])) {
            return parts[i].replace(/-/g, '_');
        }
    }
    return null;
}

// ── Editar ──────────────────────────────────────────────────────────────────
function editComponent(editor) {
    var comp = getSelectedAny(editor);
    if (!comp) return;
    if (activeInlineEdit) finishInlineEdit(editor);

    var tempId = 'ai-edit-' + Date.now();
    comp.setAttribute('data-ai-edit-id', tempId);

    var clone = comp.cloneNode(true);
    var rmAttrs = [BLOCK_ATTR, SPAN_ATTR, DEPTH_ATTR, ENTERED_ATTR,
        'data-ai-edit-id', 'contenteditable', 'data-mce-style', 'data-mce-selected'];
    rmAttrs.forEach(function (a) { clone.removeAttribute(a); });
    clone.querySelectorAll('[contenteditable]').forEach(function (c) { c.removeAttribute('contenteditable'); });
    clone.querySelectorAll(BLOCK_SEL).forEach(function (c) { c.removeAttribute(BLOCK_ATTR); });
    clone.querySelectorAll(SPAN_SEL).forEach(function (c) { c.removeAttribute(SPAN_ATTR); });
    clone.querySelectorAll('[' + DEPTH_ATTR + ']').forEach(function (c) { c.removeAttribute(DEPTH_ATTR); });
    clone.querySelectorAll('[' + ENTERED_ATTR + ']').forEach(function (c) { c.removeAttribute(ENTERED_ATTR); });

    var componentTag = detectComponentTag(comp);
    if (window.$events) {
        window.$events.emit('ai-component-library::open-with-html', {
            html: clone.outerHTML,
            nodeKey: tempId,
            componentTag: componentTag,
        });
    }
}

// ── Duplicar ────────────────────────────────────────────────────────────────
function duplicateComponent(editor) {
    var comp = getSelectedAny(editor);
    if (!comp) return;
    var clone = comp.cloneNode(true);
    if (comp.hasAttribute(SPAN_ATTR)) {
        comp.parentNode.insertBefore(document.createTextNode(' '), comp.nextSibling);
        comp.parentNode.insertBefore(clone, comp.nextSibling ? comp.nextSibling.nextSibling : null);
    } else {
        comp.parentNode.insertBefore(clone, comp.nextSibling);
    }
    markAllComponents(editor);
    editor.undoManager.add();
    editor.nodeChanged();
}

// ── Mover ───────────────────────────────────────────────────────────────────
function moveUp(editor) {
    var comp = getSelectedBlock(editor);
    if (!comp || !comp.previousElementSibling) return;
    comp.parentNode.insertBefore(comp, comp.previousElementSibling);
    editor.selection.select(comp);
    editor.undoManager.add();
    editor.nodeChanged();
}

function moveDown(editor) {
    var comp = getSelectedBlock(editor);
    if (!comp || !comp.nextElementSibling) return;
    comp.parentNode.insertBefore(comp.nextElementSibling, comp);
    editor.selection.select(comp);
    editor.undoManager.add();
    editor.nodeChanged();
}

// ── Excluir ─────────────────────────────────────────────────────────────────
function deleteComponent(editor) {
    var comp = getSelectedAny(editor);
    if (!comp) return;
    var idx = enteredStack.indexOf(comp);
    if (idx !== -1) enteredStack.splice(idx, 1);
    editor.dom.remove(comp);
    editor.undoManager.add();
    editor.nodeChanged();
}

// ── Adicionar: Duplicar e Editar ────────────────────────────────────────────
function addDuplicateAndEdit(editor) {
    var comp = getSelectedAny(editor);
    if (!comp) return;
    var clone = comp.cloneNode(true);

    if (comp.hasAttribute(SPAN_ATTR)) {
        comp.parentNode.insertBefore(document.createTextNode(' '), comp.nextSibling);
        comp.parentNode.insertBefore(clone, comp.nextSibling ? comp.nextSibling.nextSibling : null);
        clone.setAttribute(SPAN_ATTR, 'true');
        clone.setAttribute(DEPTH_ATTR, comp.getAttribute(DEPTH_ATTR) || '0');
        clone.contentEditable = 'false';
        editor.undoManager.add();
        editor.nodeChanged();
        setTimeout(function () {
            editor.selection.select(clone);
            editor.nodeChanged();
            startInlineEdit(editor, clone, clone.parentElement);
        }, 80);
    } else {
        comp.parentNode.insertBefore(clone, comp.nextSibling);
        markAllComponents(editor);
        editor.undoManager.add();
        editor.nodeChanged();
        setTimeout(function () {
            var tempId = 'ai-edit-' + Date.now();
            clone.setAttribute('data-ai-edit-id', tempId);
            var cleanClone = clone.cloneNode(true);
            var rm = [BLOCK_ATTR, SPAN_ATTR, DEPTH_ATTR, ENTERED_ATTR,
                'data-ai-edit-id', 'contenteditable', 'data-mce-style', 'data-mce-selected'];
            rm.forEach(function (a) { cleanClone.removeAttribute(a); });
            cleanClone.querySelectorAll('[contenteditable]').forEach(function (c) { c.removeAttribute('contenteditable'); });
            cleanClone.querySelectorAll(BLOCK_SEL).forEach(function (c) { c.removeAttribute(BLOCK_ATTR); });
            cleanClone.querySelectorAll(SPAN_SEL).forEach(function (c) { c.removeAttribute(SPAN_ATTR); });
            var tag = detectComponentTag(clone);
            if (window.$events) {
                window.$events.emit('ai-component-library::open-with-html', {
                    html: cleanClone.outerHTML, nodeKey: tempId, componentTag: tag,
                });
            }
        }, 80);
    }
}

// ── Adicionar: Inserir da Biblioteca ────────────────────────────────────────
function addFromLibrary(editor) {
    var comp = getSelectedAny(editor);
    if (comp) {
        try {
            var rng = editor.dom.createRng();
            rng.setStartAfter(comp);
            rng.setEndAfter(comp);
            editor.selection.setRng(rng);
        } catch (_) { }
    }
    if (window.$events) {
        window.$events.emit('ai-component-library::open');
    }
}

// ── Adicionar: Inserir componente DENTRO do bloco selecionado ────────────────
// Entra no bloco (se ainda não entrou), posiciona o cursor ao final do conteúdo
// do bloco e abre a biblioteca para inserir um filho.
function addInsideComponent(editor) {
    var comp = getSelectedBlock(editor);
    if (!comp) return;

    // Entra no bloco se ainda não foi entrado
    enterComponent(editor, comp);

    // Move cursor para o final do conteúdo do bloco
    try {
        var rng = editor.dom.createRng();
        rng.selectNodeContents(comp);
        rng.collapse(false);
        editor.selection.setRng(rng);
    } catch (_) { }

    // Abre a biblioteca de componentes
    if (window.$events) {
        window.$events.emit('ai-component-library::open');
    }
}

// ── Breadcrumb de profundidade ───────────────────────────────────────────────
// Mostra um indicador visual ("Editando: cover › badges") posicionado abaixo
// da toolbar do TinyMCE, dentro do documento principal (não no iframe).
function updateBreadcrumb(editor) {
    var BREADCRUMB_ID = 'ai-comp-depth-breadcrumb';

    // Localiza ou cria o elemento de breadcrumb
    var crumb = document.getElementById(BREADCRUMB_ID);
    if (!crumb) {
        crumb = document.createElement('div');
        crumb.id = BREADCRUMB_ID;
        crumb.className = 'ai-comp-breadcrumb';
        document.body.appendChild(crumb);
    }

    // Sem pilha: esconde o breadcrumb
    if (!enteredStack || enteredStack.length === 0) {
        crumb.style.display = 'none';
        return;
    }

    // Monta o caminho: pega o nome/classe de cada bloco na pilha
    var parts = enteredStack.map(function (el) {
        // Tenta usar o primeiro elemento filho com classe relevante como rótulo
        var label = el.getAttribute('data-ai-comp-label')
            || el.className.split(' ').find(function (c) { return c && c.length > 1 && c !== 'ai-component-block'; })
            || 'bloco';
        return label;
    });
    crumb.textContent = '📍 ' + parts.join(' › ');

    // Posiciona o breadcrumb abaixo da toolbar do editor (tox-toolbar-sticky)
    var toolbar = document.querySelector('.tox-toolbar-sticky, .tox .tox-toolbar:first-child, .tox-tinymce .tox-toolbar-overlord');
    if (toolbar) {
        var rect = toolbar.getBoundingClientRect();
        crumb.style.left = Math.round(rect.left + 8) + 'px';
        crumb.style.top = Math.round(rect.bottom + 4) + 'px';
    }

    crumb.style.display = 'flex';
}

// ═══════════════════════════════════════════════════════════════════════════════
// REGISTRO DO PLUGIN
// ═══════════════════════════════════════════════════════════════════════════════

function register(editor) {

    // ── Config dinâmica ─────────────────────────────────────────────────────
    editor.on('PreInit', function () {
        loadDetectionConfig().then(function () {
            setupInputFilter(editor);
            setupOutputFilter(editor);
        });
    });

    // ══════════════════════════════════════════════════════════════════════════
    // BOTÕES
    // ══════════════════════════════════════════════════════════════════════════

    // ── Bloco raiz ──────────────────────────────────────────────────────────
    editor.ui.registry.addButton('aicompedit', {
        tooltip: 'Editar componente',
        icon: 'edit-block',
        onAction: function () { editComponent(editor); },
    });
    editor.ui.registry.addButton('aicompduplicate', {
        tooltip: 'Duplicar componente',
        icon: 'duplicate-row',
        onAction: function () { duplicateComponent(editor); },
    });
    editor.ui.registry.addButton('aicompmoveup', {
        tooltip: 'Mover para cima',
        icon: 'chevron-up',
        onAction: function () { moveUp(editor); },
    });
    editor.ui.registry.addButton('aicompmovedown', {
        tooltip: 'Mover para baixo',
        icon: 'chevron-down',
        onAction: function () { moveDown(editor); },
    });
    editor.ui.registry.addButton('aicompdelete', {
        tooltip: 'Excluir componente',
        icon: 'remove',
        onAction: function () { deleteComponent(editor); },
    });
    editor.ui.registry.addButton('aicompchild', {
        tooltip: 'Inserir componente filho dentro',
        icon: 'indent',
        onAction: function () { addInsideComponent(editor); },
    });

    // ── Botões comuns: + (duplicar e editar), Biblioteca, Excluir ──────────
    editor.ui.registry.addButton('aiadd', {
        tooltip: 'Duplicar e editar',
        icon: 'plus',
        onAction: function () { addDuplicateAndEdit(editor); },
    });
    editor.ui.registry.addButton('ailibrary', {
        tooltip: 'Inserir da biblioteca',
        icon: 'browse',
        onAction: function () { addFromLibrary(editor); },
    });
    editor.ui.registry.addButton('aideleteinner', {
        tooltip: 'Excluir',
        icon: 'remove',
        onAction: function () { deleteComponent(editor); },
    });

    // ══════════════════════════════════════════════════════════════════════════
    // CONTEXT TOOLBARS
    // ══════════════════════════════════════════════════════════════════════════

    /** Toolbar para blocos RAIZ (não entrado e não dentro de entered). */
    editor.ui.registry.addContextToolbar('aiRootBlockToolbar', {
        predicate: function (node) {
            if (activeInlineEdit) return false;
            if (!node || !node.hasAttribute) return false;
            if (!node.hasAttribute(BLOCK_ATTR)) return false;
            if (node.hasAttribute(ENTERED_ATTR)) return false;
            if (hasEnteredAncestor(node)) return false;
            return true;
        },
        items: 'aicompedit aicompduplicate aicompmoveup aicompmovedown aicompdelete aicompchild',
        position: 'node',
        scope: 'node',
    });

    /** Toolbar para blocos INTERNOS (dentro de um bloco entered). */
    editor.ui.registry.addContextToolbar('aiInnerBlockToolbar', {
        predicate: function (node) {
            if (activeInlineEdit) return false;
            if (!node || !node.hasAttribute) return false;
            if (!node.hasAttribute(BLOCK_ATTR)) return false;
            if (node.hasAttribute(ENTERED_ATTR)) return false;
            return hasEnteredAncestor(node);
        },
        items: 'aiadd ailibrary aideleteinner',
        position: 'node',
        scope: 'node',
    });

    /** Toolbar para BADGES (standalone ou dentro de entered). */
    editor.ui.registry.addContextToolbar('aiBadgeToolbar', {
        predicate: function (node) {
            if (activeInlineEdit) return false;
            if (!node || !node.hasAttribute) return false;
            return node.hasAttribute(SPAN_ATTR);
        },
        items: 'aiadd ailibrary aideleteinner',
        position: 'node',
        scope: 'node',
    });

    // ══════════════════════════════════════════════════════════════════════════
    // EVENT HANDLERS
    // ══════════════════════════════════════════════════════════════════════════

    /**
     * AUTO-DETECT DRILL-DOWN  (chain-based, N níveis)
     *
     * Coleta a cadeia de componentes AI do target até o body.
     * Entra automaticamente em todos os níveis intermediários
     * e seleciona o componente mais interno — em um único click.
     *
     * Exemplos:
     *   page-header > badges > badge-blue  (click no badge-blue)
     *   → enter page-header, enter badges, select badge-blue
     *
     *   page-header > h1 (click no título, sem sub-componente)
     *   → chain=[page-header], length=1, seleção raiz normal
     */
    editor.on('click', function (e) {
        var target = e.target;
        var body = editor.getBody();
        if (!target || !body) return;

        // Coleta cadeia: [innermost, ..., outermost]
        var chain = collectComponentChain(target, body);

        // ── Click fora de qualquer componente AI → sair de tudo ─────────
        if (chain.length === 0) {
            if (enteredStack.length > 0) exitAllLevels(editor);
            return;
        }

        // ── Componente único (sem sub-comp) → seleção raiz normal ───────
        if (chain.length === 1) {
            // Se está entered → cursor de texto (TinyMCE cuida)
            // Se não está → TinyMCE seleciona como objeto
            return;
        }

        // ── Múltiplos componentes: drill-down automático ────────────────
        var selectTarget = chain[0]; // O mais interno

        // Se o alvo mais interno já está entered,
        // estamos clicando dentro da área editável → não interferir
        if (selectTarget.hasAttribute(ENTERED_ATTR)) return;

        // Entra em todos os intermediários (outermost → penúltimo)
        for (var i = chain.length - 1; i >= 1; i--) {
            if (!chain[i].hasAttribute(ENTERED_ATTR)) {
                enterComponent(editor, chain[i]);
            }
        }

        // Seleciona o alvo mais interno (agora contenteditable=false)
        requestAnimationFrame(function () {
            try {
                editor.selection.select(selectTarget);
            } catch (_) { }
            editor.nodeChanged();
        });
    });

    /**
     * DOUBLE-CLICK: inline edit em badges e texto leaf.
     */
    editor.on('dblclick', function (e) {
        var target = e.target;
        if (!target) return;

        // Badge: inline edit direto
        var badge = target.closest ? target.closest(SPAN_SEL) : null;
        if (badge) {
            e.preventDefault();
            e.stopPropagation();
            startInlineEdit(editor, badge, badge.parentElement);
            return;
        }

        // Texto dentro de bloco não-entered → inline edit
        var block = target.closest ? target.closest(BLOCK_SEL) : null;
        if (block && !block.hasAttribute(ENTERED_ATTR)) {
            var zone = findEditableZone(target, block);
            if (zone) {
                e.preventDefault();
                e.stopPropagation();
                startInlineEdit(editor, zone, block);
            }
        }
    });

    /** Finalizar inline edit ao clicar fora. */
    editor.on('click', function (e) {
        if (!activeInlineEdit) return;
        var el = activeInlineEdit.el;
        if (!el.contains(e.target) && e.target !== el) {
            finishInlineEdit(editor);
        }
    });

    /** ESC: sobe um nível. */
    editor.on('keydown', function (e) {
        if (e.key !== 'Escape') return;

        if (activeInlineEdit) {
            e.preventDefault();
            var el = activeInlineEdit.el;
            el.innerHTML = activeInlineEdit.originalHtml;
            finishInlineEdit(editor);
            return;
        }

        if (enteredStack.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            exitCurrentLevel(editor);
        }
    });

    // ── Marcação de componentes ─────────────────────────────────────────────
    editor.on('init', function () {
        markAllComponents(editor);
    });

    editor.on('SetContent', function () {
        markAllComponents(editor);
    });

    // ── Substituição in-place vinda da Biblioteca de Componentes ────────────
    if (window.$events) {
        window.$events.listen('editor::replace-node', function (payload) {
            var html = payload.html;
            var nodeKey = payload.nodeKey;
            var body = editor.getBody();
            if (!body || !nodeKey) return;

            var target = body.querySelector('[data-ai-edit-id="' + nodeKey + '"]');
            if (!target) return;

            var temp = editor.dom.create('div');
            temp.innerHTML = html;
            var newEl = temp.firstElementChild || temp;

            target.parentNode.replaceChild(newEl, target);

            markAllComponents(editor);
            editor.undoManager.add();
            editor.nodeChanged();
        });
    }
}

/**
 * @return {function}
 */
export function getAiComponentsPlugin() {
    return register;
}
